"use client";

import { type PropsWithChildren, useState } from "react";
import type { Chain } from "viem";
import { http, WagmiProvider as WagmiConfig, createConfig } from "wagmi";
import { immutableZkEvm, immutableZkEvmTestnet, mainnet } from "wagmi/chains";
import { injected, walletConnect } from "wagmi/connectors";

import { appConfig } from "@/constants";
import { EnvironmentNames } from "@/types";

// Define Immutable zkEVM Devnet chain
const immutableZkEvmDevnet: Chain = {
  id: 15003,
  name: "Immutable zkEVM Devnet",
  nativeCurrency: {
    name: "IMX",
    symbol: "IMX",
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ["https://rpc.dev.immutable.com"],
    },
  },
  blockExplorers: {
    default: {
      name: "Immutable Explorer",
      url: "https://explorer.dev.immutable.com",
    },
  },
};

const getPassportUrl = () =>
  appConfig.ENVIRONMENT === EnvironmentNames.PRODUCTION
    ? "https://play.immutable.com"
    : `https://play.${appConfig.ENVIRONMENT}.immutable.com`;

const createWagmiConfig = () =>
  createConfig({
    chains: [
      mainnet,
      immutableZkEvm,
      immutableZkEvmTestnet,
      immutableZkEvmDevnet,
    ],
    transports: {
      [mainnet.id]: http(),
      [immutableZkEvm.id]: http(),
      [immutableZkEvmTestnet.id]: http(),
      [immutableZkEvmDevnet.id]: http("https://rpc.dev.immutable.com"),
    },
    connectors: appConfig.WALLET_CONNECT_PROJECT_ID
      ? [
          injected(),
          walletConnect({
            projectId: appConfig.WALLET_CONNECT_PROJECT_ID,
            metadata: {
              name: "Immutable Passport Dashboard",
              description: "Immutable Passport Dashboard",
              url: getPassportUrl(),
              icons: [],
            },
          }),
        ]
      : [injected()],
  });

export function WagmiProvider({ children }: PropsWithChildren) {
  // Lazy initialize config on first render (not at module import time)
  const [config] = useState(createWagmiConfig);

  return <WagmiConfig config={config}>{children}</WagmiConfig>;
}
